import React, {useCallback, useEffect, useState} from "react";
import {
  IconDelete,
  IconEdit, IconFilter,
  IconPlus,
  IconSpinner,
  IconWarning,
  IconX,
} from "../../../components/icons";
import {useHeader} from "../../contexts/HeaderContext";
import {roleApi, rubriqueApi, rubriqueCategorieApi} from "../../../components/requests";
import {RoleModal} from "./components/RoleModal";
import {toast} from "react-toastify";
import Modal from "../../../components/common/Modal";
import {useAuth} from "../../contexts/AuthContext";
import {useFormik} from "formik";

type Permission = {
  vue: boolean;
  ecriture: boolean;
};

type Role = {
  id: number;
  libelle: string;
  code: string;
  permissions: Record<string, Permission>;
  used: boolean;
  partenaire?: boolean;
};

type Rubrique = {
  id: number;
  code: string;
  libelle: string;
};

const GestionRolePage = () => {
  const {setTitle} = useHeader();
  const {canWrite} = useAuth();

  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isListLoading, setIsListLoading] = useState<boolean>(false);
  const [roles, setRoles] = useState<Role[]>([]);
  const [rubriques, setRubriques] = useState<Rubrique[]>([]);
  const [rubriqueCategories, setRubriqueCategories] = useState<Rubrique[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  const [editingRole, setEditingRole] = useState<Role | null>(null);
  const [openModalSuppression, setOpenModalSuppression] = useState<any | null>(null);
  const [isDeleting, setIsDeleting] = useState<boolean>(false);
  const [searchQuery, setSearchQuery] = useState<string>('');

  // Init title
  useEffect(() => {
    setTitle("Rôles utilisateur");
  }, [setTitle]);

  // Les loaders
  // Loader Roles
  const loadRoles = useCallback(async () => {
    const res = await roleApi.getAll(searchQuery);
    const data = res.data ?? res.data.member ?? [];
    setRoles(data);
  }, [searchQuery]);

  // Loader Rubriques
  const loadRubriques = useCallback(async () => {
    const res = await rubriqueApi.getAll();
    const data = res.data.member ?? res.data["hydra:member"] ?? [];
    setRubriques(data);
  }, []);

  // Loader Rubriques Categories
  const loadRubriqueCategories = useCallback(async () => {
    const res = await rubriqueCategorieApi.getAll();
    const data = res.data.member ?? res.data["hydra:member"] ?? [];
    setRubriqueCategories(data);
  }, []);

  // Loader au début
  useEffect(() => {
    const init = async () => {
      setIsListLoading(true);
      try {
        await Promise.all([loadRoles(), loadRubriques(), loadRubriqueCategories()]);
      } catch (e) {
        console.error("Erreur chargement :", e);
      } finally {
        setIsLoading(false);
        setIsListLoading(false);
      }
    };

    init();
  }, [loadRoles, loadRubriques, loadRubriqueCategories]);

  // Pour la création
  const handleCreate = useCallback(async (data: Role) => {
    try {
      const resCreate = await roleApi.create(data);
      const dataUpd = {
        ...data,
        id: resCreate.data.id,
        used: false
      }

      if (resCreate.status === 201) {
        setRoles((prevRole => [
          ...prevRole,
          dataUpd
        ]))
        toast.success("Rôle créé !")
      } else {
        toast.error("Erreur lors de la création du rôle !")
      }

      setIsOpen(false);
    } catch (e) {
      toast.error("Erreur lors de la création du rôle !")
    }
  }, []);

  // Pour la modification
  const handleEdit = useCallback(async (data: Role) => {
    try {
      const resEdit = await roleApi.update(data.id, data);

      if (resEdit.status === 201) {
        setRoles((prevRoles) =>
          prevRoles.map((role) =>
            role.id === data.id ? data : role
          )
        );
        toast.success("Rôle modifié !")
      } else {
        toast.error("Erreur lors de la modification du rôle !")
      }

      setEditingRole(null);
      setIsOpen(false);
    } catch (e) {
      toast.error("Erreur lors de la modification du rôle !")
    }
  }, []);

  // Pour la suppression
  const handleDelete = useCallback(async (id: number) => {
    toast.info("Suppression en cours !");
    setIsDeleting(true);
    roleApi
      .delete(id)
      .then((res) => {
        if (res.status === 200) {
          setRoles((prevRole) => prevRole.filter((role) => role.id !== id));
          toast.success("Rôle supprimé !");
        } else if (res.status === 400) {
          toast.error(res.data.error);
        }
      })
      .catch((error) => console.error(error))
      .finally(() => {
        setIsDeleting(false);
        setOpenModalSuppression(null);
      });
  }, []);

  const formik = useFormik({
    initialValues: {
      search: '',
    },
    onSubmit: values => {
      setSearchQuery(values?.search?.trim());
    },
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen-fit">
        <IconSpinner className="animate-spin w-10 h-10"/>
      </div>
    );
  }

  return (
    <div className="bg-gray-50 min-h-screen-fit animate-in fade-in duration-300">
      {/* HEADER */}
      <div className="sticky top-0 bg-gray-50 py-2 px-4">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-bold text-gray-900">Tous les rôles</h3>
            <p className="text-sm text-gray-500">
              {roles.length} rôle(s) au total
            </p>
          </div>

          {canWrite('Parametrage') &&
            <button
              onClick={() => {
                setEditingRole(null);
                setIsOpen(true);
              }}
              className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg text-sm font-medium flex items-center gap-2"
            >
              <IconPlus/>
              Créer un rôle
            </button>
          }
        </div>
      </div>

      <form onSubmit={formik.handleSubmit}>
        <div className="flex justify-end pb-2 px-4">
          <div className="flex gap-4 w-full">
            <div className="flex-1">
              <label className="form-label">Recherche</label>
              <input
                name="search"
                type="text"
                className="w-full form-input"
                onChange={formik.handleChange}
              />
            </div>
            <div className="flex items-end">
              <button
                type="submit"
                className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg text-sm font-medium flex items-center gap-2"
              >
                <IconFilter/>
                Filtrer
              </button>
            </div>
          </div>
        </div>
      </form>

      {/* TABLE */}
      <div className="px-4">
        <div className="bg-white rounded-xl shadow-sm overflow-hidden">
          <table className="w-full">
            <thead className="bg-gray-50 text-xs uppercase text-gray-500">
            <tr>
              <th className="px-5 py-3 text-left font-semibold">Code</th>
              <th className="px-5 py-3 text-left font-semibold">Libellé</th>
              {canWrite('Parametrage') &&
                <th className="px-5 py-3 text-left font-semibold w-[100px]">Action</th>
              }
            </tr>
            </thead>

            <tbody className="divide-y divide-gray-100">
            {isListLoading ? (
              <tr>
                <td colSpan={12}>
                  <div className="flex items-center justify-center min-h-[300px]">
                    <IconSpinner className="animate-spin w-10 h-10"/>
                  </div>
                </td>
              </tr>
            ) : roles.length === 0 ? (
              <tr>
                <td colSpan={12}>
                  <div className="flex items-center justify-center text-sm text-gray-400 min-h-[300px]">
                    Aucun rôle
                  </div>
                </td>
              </tr>
            ) : (
              roles.map((role) => (
                <tr key={role.id} className="hover:bg-gray-50">
                  <td className="px-5 py-4 text-gray-700">
                    <p className="font-medium text-gray-900 text-sm">
                      {role.code}
                    </p>
                  </td>
                  <td className="px-5 py-4 text-gray-700">
                    <p className="text-gray-700 text-sm">{role.libelle}</p>
                  </td>
                  {canWrite('Parametrage') &&
                    <td className="px-5 py-4 space-x-3">
                      <div className="grid grid-cols-2 gap-1">
                        <button
                          onClick={() => {
                            setEditingRole(role);
                            setIsOpen(true);
                          }}
                          className="w-7 h-7 rounded text-xs font-medium flex justify-center items-center bg-green-50 text-green-600 hover:bg-green-100"
                          title="Modifier">
                          <IconEdit/>
                        </button>

                        {!role.used && (
                          <button
                            onClick={() => setOpenModalSuppression(role.id)}
                            className="w-7 h-7 rounded text-xs font-medium flex justify-center items-center bg-red-50 text-red-600 hover:bg-red-100"
                            title="Supprimer">
                            <IconDelete/>
                          </button>
                        )}
                      </div>
                    </td>
                  }
                </tr>
              ))
            )}
            </tbody>
          </table>
        </div>
      </div>

      {/* MODAL creation / modification */}
      <RoleModal
        isOpen={isOpen}
        initialData={editingRole || undefined}
        rubriques={rubriques}
        rubriqueCategories={rubriqueCategories}
        onClose={() => {
          setIsOpen(false);
          setEditingRole(null);
        }}
        onSubmit={editingRole ? handleEdit : handleCreate}
      />

      {/* MODAL suppression */}
      <Modal openModal={openModalSuppression}>
        <div className="section-header flex justify-between">
          <div className="flex items-center gap-3">
            <div className="section-icon bg-red-100 text-red-600">
              <IconWarning/>
            </div>
            <span className="section-title">Supprimer le rôle utilisateur</span>
          </div>
          <button onClick={() => setOpenModalSuppression(null)}>
            <IconX/>
          </button>
        </div>

        <div className="section-body p-3 text-sm">
          <p className="text-gray-600">Êtes-vous sûr de vouloir supprimer ce rôle ?</p>
          <p className="text-red-600">Cette action est irréversible.</p>
        </div>

        <div className="section-footer flex justify-end">
          <button
            onClick={() => setOpenModalSuppression(null)}
            className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg text-sm font-medium flex items-center gap-2">
            Annuler
          </button>
          <button
            onClick={() => handleDelete(openModalSuppression)}
            className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg text-sm font-medium flex items-center gap-2">
            {isDeleting ? "Suppression..." : "Valider"}
          </button>
        </div>
      </Modal>
    </div>
  );
};

export default GestionRolePage;